import React from 'react';

import { Box, Collapse, Paper, Typography } from '@mui/material';
import { AttachFile, EmailOutlined, ExpandLess, ExpandMore } from '@mui/icons-material';
import { ConversationActivity, PatientActivity, PatientActivityType } from '@uplift/api';
import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { HandshakeIcon } from '@uplift/icons';
import { CalendarDateService } from '@uplift/services';
import { isIntakeForm, SubSystem } from '@uplift/types';
import {
  MessageAttachmentsList, MessageItem, Button,
} from '@uplift/ui-v2';
import { formatReferrerNameWithCredentials, getCareTypeLabel } from '@uplift/utils';
import {
  AssessmentFormActivityItem, BaseActivityItem, BaseNoteActivityItem, DocumentActivityItem,
  NewMatchByAdminActivityItem, NewMatchByReferralActivityItem, FormActivityItem,
  NewMatchByRematchActivityItem, PsychiatrySessionBaseActivityItem,
  PsychiatrySessionCompletedActivityItem, PsychiatrySessionCompletedOnCanvasActivityItem,
  PsychiatrySessionSubmittedActivityItem, RematchActivityItem, SessionCancelledActivityItem, TherapySessionBaseActivityItem,
  TherapySessionCompletedActivityItem, TherapySessionSubmittedActivityItem
} from '../../../../components';

export type ActivityItemProps = {
  activity: PatientActivity;
  onActivityClick: () => void;
  onDocumentClick?: () => void;
};


const ConversationActivityItem: React.FC<{ activity: ConversationActivity }> = ({ activity }) => {
  const me = useCurrentuser();
  const [ opened, setOpened ] = React.useState(false);
  const messagesCount = activity.messages.length;

  const messages = React.useMemo(() => {
    const reversable = [ ...activity.messages ];

    reversable.reverse();
    return reversable;
  }, [ activity.messages ]);

  const attachments = React.useMemo(() => {
    return messages.map(msg => msg.attachments ?? []).flat();
  }, [ messages ]);

  return (
    <BaseActivityItem
      title={<Typography>Conversation</Typography>}
      rightContent={(
        <Button endIcon={opened ? <ExpandLess /> : <ExpandMore />} size="small" onClick={() => setOpened(!opened)}>
          Expand conversation
        </Button>
      )}
      icon={EmailOutlined}
      color="secondary"
      testId="partner-referral-activity-item"
    >
      <Box>
        <Typography color="textSecondary">{messages.length} message{messagesCount > 1 ? 's' : ''}</Typography>
        {attachments?.length ? (
          <Paper variant="outlined" sx={{ mt: 1 }}>
            <MessageAttachmentsList attachments={attachments} downloadableAttachments />
          </Paper>
        ) : null}
        <Box sx={{ mt: opened ? 1 : 0, transition: 'margin-top 0.3s' }}>
          <Collapse in={opened}>
            <Typography color="textSecondary" data-testid="referral-care-type">
              {messages.map(msg => (
                <MessageItem color="secondary" key={msg.id} message={msg} direction={me.id === msg.receiverId ? 'receive' : 'send'} />
              ))}
            </Typography>
          </Collapse>
        </Box>
      </Box>
    </BaseActivityItem>
  );
};


export const ActivityItem: React.FC<ActivityItemProps> = React.memo(({ activity, onActivityClick, onDocumentClick }) => {
  const timezone = useTimezone();

  switch (activity.activityType) {
    case PatientActivityType.newMatchByAdmin:
      return (
        <NewMatchByAdminActivityItem
          key={activity.datetime}
          match={activity.match}
        />
      );
    case PatientActivityType.newMatchByReferral:
      return (
        <NewMatchByReferralActivityItem
          key={activity.datetime}
          match={activity.match}
          onActivityClick={onActivityClick}
        />
      );
    case PatientActivityType.newMatchByRematch:
    case PatientActivityType.newMatchByPsychiatryBooking:
      return (
        <NewMatchByRematchActivityItem
          key={activity.datetime}
          match={activity.match}
        />
      );
    case PatientActivityType.rematched:
      return (
        <RematchActivityItem
          key={activity.datetime}
          match={activity.match}
        />
      );
    case PatientActivityType.assessmentFormInitial:
    case PatientActivityType.assessmentFormExpired:
    case PatientActivityType.assessmentFormCompleted:
      return (
        <AssessmentFormActivityItem
          onClick={onActivityClick}
          key={activity.submission.formSubmissionId}
          title={activity.form.name + ' Assessment'}
          assessmentKey={activity.formKey}
          percentage={activity.scoreDiffPercentage}
          score={activity.score}
          completedAt={activity.submission.completedAt}
          expiredAt={activity.submission.expiredAt}
        />
      );
    case PatientActivityType.formInitial:
    case PatientActivityType.formExpired:
    case PatientActivityType.formCompleted:
      if (isIntakeForm(activity.formKey)) {
        return (
          <FormActivityItem
            key={activity.submission.formSubmissionId}
            onClick={onActivityClick}
            title={activity.form.name}
            completedAt={activity.submission.completedAt}
            expiredAt={activity.submission.expiredAt}
          >
            <Typography color="textSecondary">
              <strong>Reason for visit</strong>: {activity.details.reasonsForVisit || 'None'}
              {activity.submission.completedAt && (<>{'. '} <Typography component="span">Completed {CalendarDateService.format(activity.submission.completedAt, 'M/D/YYYY', 'M/D/YYYY', { timezone })}</Typography></>)}
            </Typography>
          </FormActivityItem>
        );
      } else {
        return (
          <FormActivityItem
            key={activity.submission.formSubmissionId}
            onClick={activity.submission.completedAt && onActivityClick}
            title={activity.form.name}
            completedAt={activity.submission.completedAt}
            expiredAt={activity.submission.expiredAt}
          />
        );
      }

    case PatientActivityType.therapySessionCancelled:
    case PatientActivityType.psychiatrySessionCancelled:
      return (
        <SessionCancelledActivityItem
          key={activity.appointment.id}
          activity={activity}
          onClick={onActivityClick}
        />
      );

    case PatientActivityType.therapySessionInitial:
      return (
        <TherapySessionBaseActivityItem
          key={activity.appointment.id}
          appointment={activity.appointment}
          provider={activity.provider}
          notes={activity.clinicalNotes}
        />
      );
    case PatientActivityType.therapySessionCompleted:
      return (
        <TherapySessionCompletedActivityItem
          key={activity.appointment.id}
          onClick={onActivityClick}
          appointment={activity.appointment}
          provider={activity.provider}
          notes={activity.clinicalNotes}
        />
      );
    case PatientActivityType.therapySessionSubmitted:
      return (
        <TherapySessionSubmittedActivityItem
          key={activity.appointment.id}
          onClick={onActivityClick}
          appointment={activity.appointment}
          provider={activity.provider}
          notes={activity.clinicalNotes}
        />
      );
    case PatientActivityType.psychiatrySessionInitial:
      return (
        <PsychiatrySessionBaseActivityItem
          key={activity.appointment.id}
          isFirstSession={activity.isFirstSession}
          appointment={activity.appointment}
          provider={activity.provider}
        />
      );
    case PatientActivityType.psychiatrySessionCompleted:
      return (
        <PsychiatrySessionCompletedActivityItem
          key={activity.appointment.id}
          isFirstSession={activity.isFirstSession}
          appointment={activity.appointment}
          provider={activity.provider}
        />
      );
    case PatientActivityType.psychiatrySessionSubmitted:
      return (
        <PsychiatrySessionSubmittedActivityItem
          key={activity.appointment.id}
          isFirstSession={activity.isFirstSession}
          appointment={activity.appointment}
          provider={activity.provider}
          onClick={onActivityClick}
          plan={activity.plan}
        />
      );
    case PatientActivityType.psychiatrySessionCompletedOnCanvas:
      return (
        <PsychiatrySessionCompletedOnCanvasActivityItem
          key={activity.appointment.id}
          isFirstSession={activity.isFirstSession}
          appointment={activity.appointment}
          provider={activity.provider}
        />
      );
    case PatientActivityType.document:
      return (
        <DocumentActivityItem
          key={activity.datetime}
          onClick={onDocumentClick}
          document={activity.document}
        />
      );
    case PatientActivityType.noteInitial:
    case PatientActivityType.noteSubmitted:
      return (
        <BaseNoteActivityItem
          key={activity.note.id}
          note={activity.note}
          noteType={activity.note.noteType}
          provider={activity.provider}
          onClick={onActivityClick}
        />
      );
    case PatientActivityType.referral: {
      const partnerReferralActivity = activity;
      const isPartnerReferral = partnerReferralActivity?.fromUser?.user?.subsystem === SubSystem.higherEd;
      const referrerNameWithCredentials = formatReferrerNameWithCredentials(partnerReferralActivity.fromUser?.user, partnerReferralActivity.fromUser?.profile);
      const studentName = partnerReferralActivity.aboutUser?.profile?.preferredName ?? partnerReferralActivity.aboutUser?.user?.firstName;

      return (
        <BaseActivityItem
          onClick={onActivityClick}
          title={<Typography>Partner Referral</Typography>}
          icon={HandshakeIcon}
          color="secondary"
          rightContent={(
            <Typography variant="body2" color="textPrimary" data-testid="referral-from-student-school">
              {`from ${studentName}'s school`}
            </Typography>
          )}
          testId="partner-referral-activity-item"
        >
          <Typography color="textSecondary" data-testid="referral-care-type">
            <strong>Care Type: </strong>{getCareTypeLabel(partnerReferralActivity.referral.careType)}
          </Typography>
          {partnerReferralActivity.referral.content && (
            <Box display="flex" alignItems="center" gap="4px" data-testid="referral-note-attached">
              <AttachFile style={{ fontSize: '16px', color: '#757575' }} />
              <Typography color="textSecondary" data-testid="referral-note-from">
                {isPartnerReferral ? `Note from ${referrerNameWithCredentials}` : 'Referral Note'}
              </Typography>
            </Box>
          )}
        </BaseActivityItem>
      );
    }
    case PatientActivityType.conversation: {
      return (
        <ConversationActivityItem activity={activity} />
      );
    }
    default:
  }

  return null;
});
